import { useState, useEffect, useMemo } from "react";
import {
  Avatar,
  Button,
  Card,
  Col,
  Flex,
  Popconfirm,
  Radio,
  Rate,
  Row,
  Tag,
  Typography,
  Pagination,
  Skeleton,
  Empty,
  message,
} from "antd";
import { Dropdown, Menu } from "antd";
import { EllipsisOutlined } from "@ant-design/icons";

import { useParams } from "react-router";
import { useSelector } from "react-redux";
import PageHeader from "../../components/PageHeader";
import { useAPI } from "../../hooks/useAPI";
import { getCall, updateCall } from "../../constants/apicalls.constant";
import { RootState } from "../../store/store";
import {
  ReviewParameterKey,
  ReviewSummaryApiResponse,
  ReviewListApiResponse,
  ReviewItem,
  ReviewStatus,
} from "../../models/review";
import { STATUS } from "../../constants/status.constant";
import { EventData } from "../../models/event";
import styles from "./review.module.scss";
import { ENDPOINTS } from "../../constants/endpoints.constant";
import { formatDisplayDate } from "../../utils/DateAndTimeConvertor";
import {
  extractEventReviewAverages,
  getOverallAverageFromSummary,
  normalizeReviewSummary,
} from "../../utils/eventReviewAverages";

const { Text } = Typography;

const PARAMETER_META: Record<
  ReviewParameterKey,
  { label: string; description: string }
> = {
  registration_communication: {
    label: "Registration Communication",
    description: "Clarity of registration & updates",
  },
  expo_experience: {
    label: "Expo Experience",
    description: "Pre-event expo quality",
  },
  event_experience: {
    label: "Event Experience",
    description: "Overall execution",
  },
  merchandise_goodie_bag: {
    label: "Merchandise",
    description: "Goodies & t-shirt quality",
  },
  event_arrangements: {
    label: "Event Arrangements",
    description: "Logistics & management",
  },
  on_course_amenities: {
    label: "On-course Amenities",
    description: "Water, hydration, etc.",
  },
  event_entertainment: {
    label: "Entertainment",
    description: "Music & engagement",
  },
  medical_facilities: {
    label: "Medical Facilities",
    description: "Safety & support",
  },
  medal_feedback: {
    label: "Medal Feedback",
    description: "Finisher medal quality",
  },
};

const REVIEW_KEYS = Object.keys(PARAMETER_META) as ReviewParameterKey[];

type ReviewStatusFilter = "all" | ReviewStatus;

const REVIEW_STATUS_LABELS: Record<ReviewStatus, string> = {
  pending: "Pending",
  approved: "Approved",
  rejected: "Rejected",
};

const getRatingColor = (value: number) => {
  if (value >= 4.5) return "#52c41a";
  if (value >= 3.5) return "#faad14";
  return "#ff4d4f";
};

const getCustomerDisplayName = (review: ReviewItem) => {
  const c = review.customer;
  const name = [c?.first_name, c?.last_name].filter(Boolean).join(" ").trim();
  if (name) return name;
  if (c?.email) return c.email;
  if (c?.phone) return c.phone;
  return "Anonymous Participant";
};

const getCustomerAvatarSrc = (review: ReviewItem) =>
  review.customer?.profile_picture?.[0]?.thumbnail ||
  review.customer?.profile_picture?.[0]?.url;

const Review = () => {
  const { id: paramId } = useParams();
  const event = useSelector((state: RootState) => state.event);
  const eventId = paramId || event?.id;

  const [currentPage, setCurrentPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<ReviewStatusFilter>("all");
  const [updatingReviewId, setUpdatingReviewId] = useState<string | null>(null);
  const pageSize = 5;

  const {
    response: summaryResponse,
    loading: summaryLoading,
    trigger: fetchReviewSummary,
  } = useAPI<ReviewSummaryApiResponse>(ENDPOINTS.get_review_parameters);

  const {
    response: reviewsResponse,
    loading: reviewsLoading,
    trigger: fetchReviews,
  } = useAPI<ReviewListApiResponse>(ENDPOINTS.get_event_review);

  const { trigger: triggerUpdateReview } = useAPI(ENDPOINTS.get_event_review);

  const summaryData = useMemo(() => {
    if (summaryResponse?.status && summaryResponse.data) {
      const fromApi = normalizeReviewSummary(
        summaryResponse.data as Record<string, unknown>,
      );
      if (fromApi) return fromApi;
    }

    return (
      extractEventReviewAverages(
        event as EventData & Record<string, unknown>,
      ) ?? undefined
    );
  }, [summaryResponse?.data, event]);

  const reviewsData = reviewsResponse?.data;
  const reviews = reviewsData?.data || [];
  const overallSummaryRating = useMemo(
    () => getOverallAverageFromSummary(summaryData ?? null),
    [summaryData],
  );

  useEffect(() => {
    if (!eventId) return;

    fetchReviewSummary(
      getCall({
        event_id: eventId,
        filter: { event_id: { eq: eventId } },
      }),
    );
  }, [eventId]);

  const loadReviews = () => {
    if (!eventId) return;

    const filter: Record<string, unknown> = {
      event_id: { eq: eventId },
    };

    if (statusFilter !== "all") {
      filter.status = { eq: statusFilter };
    }

    fetchReviews(
      getCall({
        filter,
        page: currentPage,
        per_page: pageSize,
        with: ["customer"],
      }),
    );
  };

  useEffect(() => {
    loadReviews();
  }, [eventId, currentPage, statusFilter]);

  const handleReviewStatus = async (reviewId: string, status: ReviewStatus) => {
    setUpdatingReviewId(reviewId);
    try {
      const res = await triggerUpdateReview(
        updateCall(reviewId, { status }, {}, ENDPOINTS.get_event_review),
      );

      if (res && (res as { status?: boolean }).status !== false) {
        message.success(`Review ${status} successfully`);
        loadReviews();
        fetchReviewSummary(
          getCall({
            event_id: eventId,
            filter: { event_id: { eq: eventId } },
          }),
        );
      } else {
        message.error("Failed to update review status");
      }
    } catch {
      message.error("Failed to update review status");
    } finally {
      setUpdatingReviewId(null);
    }
  };

  const getReviewStatusTag = (status?: ReviewStatus) => {
    const key = status ?? "pending";
    const colors = STATUS[key] ?? STATUS.pending;

    return (
      <Tag color={colors.bg} style={{ color: colors.text, marginInlineEnd: 0 }}>
        {REVIEW_STATUS_LABELS[key as ReviewStatus] ?? key}
      </Tag>
    );
  };

  const calculateOverallRating = (review: ReviewItem) => {
    const values = REVIEW_KEYS.map((key) => review[key] || 0).filter(
      (v) => v > 0,
    );
    if (values.length === 0) return 0;
    return parseFloat(
      (values.reduce((a, b) => a + b, 0) / values.length).toFixed(1),
    );
  };

  if (summaryLoading && !summaryData && reviewsLoading) {
    return <Skeleton active />;
  }

  const filterOptions: { value: ReviewStatusFilter; label: string }[] = [
    { value: "all", label: "All" },
    { value: "pending", label: "Pending" },
    { value: "approved", label: "Approved" },
    { value: "rejected", label: "Rejected" },
  ];

  const statusFilterControl = (
    <Radio.Group
      className={styles.statusFilter}
      value={statusFilter}
      onChange={(e) => {
        setCurrentPage(1);
        setStatusFilter(e.target.value);
      }}
      optionType="button"
      buttonStyle="solid"
    >
      <Radio.Button value="all">All</Radio.Button>
      <Radio.Button value="pending">Pending</Radio.Button>
      <Radio.Button value="approved">Approved</Radio.Button>
      <Radio.Button value="rejected">Rejected</Radio.Button>
    </Radio.Group>
  );

  const statusFilterDropdown = (
    <Dropdown
      trigger={["click"]}
      overlay={
        <Menu
          selectedKeys={[statusFilter]}
          onClick={({ key }) => {
            setCurrentPage(1);
            setStatusFilter(key as ReviewStatusFilter);
          }}
        >
          {filterOptions.map((opt) => (
            <Menu.Item key={opt.value}>{opt.label}</Menu.Item>
          ))}
        </Menu>
      }
    >
      <Button type="text" icon={<EllipsisOutlined />} />
    </Dropdown>
  );

  return (
    <div className={styles.page}>
      <PageHeader
        title="Event Reviews"
        backBtn={false}
        description="Participant feedback and ratings"
        bottomMargin={false}
      />

      <Card
        className={styles.summaryCard}
        title={
          <Flex justify="space-between" align="center" gap={12} wrap>
            <Text strong style={{ fontSize: 15 }}>
              Ratings by Parameter
            </Text>
            {overallSummaryRating > 0 && (
              <Tag
                style={{
                  background: `${getRatingColor(overallSummaryRating)}20`,
                  color: getRatingColor(overallSummaryRating),
                  marginInlineEnd: 0,
                  fontSize: 13,
                  padding: "2px 10px",
                }}
              >
                Overall ★ {overallSummaryRating}
              </Tag>
            )}
          </Flex>
        }
        loading={summaryLoading && !summaryData}
      >
        <Row gutter={[10, 10]}>
          {REVIEW_KEYS.map((key) => {
            const avg = parseFloat(summaryData?.[key] || "0");
            return (
              <Col xs={12} sm={8} key={key}>
                <div className={styles.summaryItem}>
                  <Text className={styles.summaryLabel}>
                    {PARAMETER_META[key].label}
                  </Text>
                  <div className={styles.summaryScoreRow}>
                    <Rate
                      disabled
                      allowHalf
                      value={avg}
                      className={styles.ratingSmall}
                    />
                    <span
                      className={styles.summaryScore}
                      style={{ color: getRatingColor(avg) }}
                    >
                      {avg > 0 ? avg.toFixed(1) : ""}
                    </span>
                  </div>
                </div>
              </Col>
            );
          })}
        </Row>
      </Card>

      <Card
        className={styles.reviewsSection}
        title={
          <Text strong style={{ fontSize: 15 }}>
            Reviews ({reviewsData?.total || 0})
          </Text>
        }
        extra={
          <>
            <span className={styles.filterDesktop}>{statusFilterControl}</span>
            <span className={styles.filterMobile}>{statusFilterDropdown}</span>
          </>
        }
      >
        {reviewsLoading ? (
          <Row gutter={[12, 12]} className={styles.reviewList}>
            {[1, 2, 3, 4].map((i) => (
              <Col xs={24} sm={12} key={i}>
                <Card size="small" className={styles.reviewCard}>
                  <Skeleton active avatar paragraph={{ rows: 2 }} />
                </Card>
              </Col>
            ))}
          </Row>
        ) : reviews.length ? (
          <>
            <Row gutter={[12, 12]} className={styles.reviewList}>
              {reviews.map((review) => {
                const overall = calculateOverallRating(review);
                const displayName = getCustomerDisplayName(review);
                const avatarSrc = getCustomerAvatarSrc(review);

                return (
                  <Col xs={24} sm={12} key={review.id}>
                    <Card size="small" className={styles.reviewCard}>
                      <div className={styles.reviewCardHeader}>
                        <Avatar size={32} src={avatarSrc}>
                          {displayName.charAt(0).toUpperCase()}
                        </Avatar>
                        <div className={styles.reviewCardHeaderMain}>
                          <div className={styles.reviewCardNameRow}>
                            <Text strong ellipsis className={styles.reviewName}>
                              {displayName}
                            </Text>
                            {getReviewStatusTag(review.status)}
                          </div>
                          <span className={styles.reviewCardDate}>
                            {formatDisplayDate(review.created_at)}
                          </span>
                        </div>
                      </div>

                      <div className={styles.reviewCardOverall}>
                        <div className={styles.reviewCardOverallLeft}>
                          <Rate
                            disabled
                            allowHalf
                            value={overall}
                            className={styles.ratingSmall}
                          />
                          <Text
                            strong
                            className={styles.overallScore}
                            style={{ color: getRatingColor(overall) }}
                          >
                            {overall}
                          </Text>
                        </div>

                        {review.status === "pending" && (
                          <Dropdown
                            trigger={['click']}
                            overlay={
                              <Menu>
                                <Menu.Item key="approve">
                                  <Popconfirm
                                    title="Approve this review?"
                                    description="It will be visible on the public event page."
                                    onConfirm={() => handleReviewStatus(review.id, "approved")}
                                    okText="Approve"
                                  >
                                    <span>Approve</span>
                                  </Popconfirm>
                                </Menu.Item>
                                <Menu.Item key="reject">
                                  <Popconfirm
                                    title="Reject this review?"
                                    description="It will not be shown publicly."
                                    onConfirm={() => handleReviewStatus(review.id, "rejected")}
                                    okText="Reject"
                                    okButtonProps={{ danger: true }}
                                  >
                                    <span>Reject</span>
                                  </Popconfirm>
                                </Menu.Item>
                              </Menu>
                            }
                          >
                            <Button
                              type="text"
                              icon={<EllipsisOutlined />}
                              loading={updatingReviewId === review.id}
                            />
                          </Dropdown>
                        )}
                      </div>

                      {review.review_message && (
                        <Text className={styles.reviewCardMessage}>
                          {review.review_message}
                        </Text>
                      )}

                      <div className={styles.ratingsGrid}>
                        {REVIEW_KEYS.map((key) => {
                          const val = review[key] || 0;
                          return (
                            <div key={key} className={styles.ratingChip}>
                              <Text className={styles.ratingLabel} ellipsis>
                                {PARAMETER_META[key].label}
                              </Text>
                              <Rate
                                disabled
                                allowHalf
                                value={val}
                                className={styles.ratingChipStars}
                              />
                            </div>
                          );
                        })}
                      </div>
                    </Card>
                  </Col>
                );
              })}
            </Row>

            <div className={styles.paginationWrap}>
              <Pagination
                current={reviewsData?.current_page ?? currentPage}
                pageSize={reviewsData?.per_page ?? pageSize}
                total={reviewsData?.total || 0}
                onChange={setCurrentPage}
                showSizeChanger={false}
                responsive
              />
            </div>
          </>
        ) : (
          <Empty description="No reviews found" />
        )}
      </Card>
    </div>
  );
};

export default Review;
